import { BrandsModel, inventoryApi } from '@pukka/api';
import { assertUtil } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { proxy } from '@pukka/valtio';

import { BrandsPersistedState, BrandsState, IBrandsPersistedState } from '../Brands.state';
import { BrandsFormState } from '../BrandsForm';

const BRAND_LIST_SIZE = 999;
const GENERAL_ERROR_CODE = ['UNSPECIFIED', 'ERR_NETWORK', 'ERR_BAD_RESPONSE'];

interface Brand {
  id: number;
  name: string;
}

interface Parent {
  id: number;
  name: string;
}

interface IBrandsAssignState extends Record<string, unknown> {
  isLoading: boolean;
  isLoadingList: boolean;
  brandList: BrandsModel.GetBrandListResponse | null;
  parentList: Parent[];

  selectedBrandList: Brand[];
  unselectedBrandList: Brand[];
  initialSelectedBrandIds: number[];

  selectedParent: Parent | null;
  initialSelectedParentId: number | null;

  searchFilter: string;

  sortedUnselectedBrandList: Brand[];
  sortedSelectedBrandList: Brand[];

  filteredParentList: Parent[];

  setSelectedParent(parent: Parent): void;
  handleClickBrandCheckbox(selectedBrand: Brand, checked: boolean): void;
  handleChangeSearchFilter(value: string): void;

  initParentList(selected: Parent | null, onSuccess: () => void, persistedState?: IBrandsPersistedState): void;
  initBrandList(onSuccess: () => void): void;
}

/* istanbul ignore next */
const handleMutationError = (e: unknown) => {
  assertUtil.assertObjectIsTruthy<{
    code: string;
    message: string;
  }>(e);

  /* istanbul ignore next */
  if (GENERAL_ERROR_CODE.includes(e.code)) {
    toast.error('A technical error has occurred. Please try again.');
  } else {
    toast.error(e.message || 'A technical error has occurred. Please try again.');
  }
};

const isEmpty = (variant: 'parent' | 'brand', state: IBrandsAssignState) => {
  if (variant === 'parent') {
    return !state.isLoadingList && state.parentList.length === 1 && state.parentList[0].id === -1;
  }

  return !state.isLoadingList && state.brandList?.results.length === 0;
};

const isDirty = (variant: 'parent' | 'brand', state: IBrandsAssignState) => {
  if (variant === 'brand') {
    const currentIds = state.selectedBrandList.map((brand) => brand.id);

    return JSON.stringify(state.initialSelectedBrandIds.sort()) !== JSON.stringify(currentIds.sort());
  }

  const currentParentId = state.selectedParent?.id === -1 ? null : state.selectedParent?.id;
  const initialParentId = state.initialSelectedParentId === -1 ? null : state.initialSelectedParentId;

  // for parent
  return currentParentId !== initialParentId;
};

const BrandsAssignState: IBrandsAssignState = proxy({
  isLoading: false,
  isLoadingList: false,

  isComplete: false,

  brandList: null, // original brand list
  unselectedBrandList: [],
  selectedBrandList: [],
  initialSelectedBrandIds: [],

  parentList: [],
  selectedParent: null,
  initialSelectedParentId: null,

  searchFilter: '',

  get sortedUnselectedBrandList() {
    return BrandsAssignState.unselectedBrandList
      .filter((brand) => brand.name.toLowerCase().includes(BrandsAssignState.searchFilter.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  },
  get sortedSelectedBrandList() {
    return BrandsAssignState.selectedBrandList
      .filter((brand) => brand.name.toLowerCase().includes(BrandsAssignState.searchFilter.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  },
  get filteredParentList() {
    return BrandsAssignState.parentList.filter((parent) =>
      parent.name.toLowerCase().includes(BrandsAssignState.searchFilter.toLowerCase()),
    );
  },

  setSelectedParent: (parent: Parent) => {
    BrandsAssignState.selectedParent = parent;
  },

  handleClickBrandCheckbox: (selectedBrand: Brand, checked: boolean) => {
    if (checked) {
      BrandsAssignState.selectedBrandList.push(selectedBrand);
      BrandsAssignState.unselectedBrandList = BrandsAssignState.unselectedBrandList.filter((brand) => brand.id !== selectedBrand.id);
    } else {
      BrandsAssignState.selectedBrandList = BrandsAssignState.selectedBrandList.filter((brand) => brand.id !== selectedBrand.id);
      BrandsAssignState.unselectedBrandList.push(selectedBrand);
    }
  },

  handleChangeSearchFilter: (value: string) => {
    BrandsAssignState.searchFilter = value;
  },

  initParentList: async (selected: Parent | null, onSuccess: () => void, persistedState = BrandsPersistedState) => {
    const parentList = (persistedState.parentList?.results || []).map((p) => ({
      id: p.id,
      name: p.name,
    }));

    const hasUnprincipal = parentList.some((parent) => parent.id === -1);

    if (!hasUnprincipal) {
      parentList.unshift({ id: -1, name: 'None' });
    }

    BrandsAssignState.parentList = parentList;

    const initialSelected = selected || {
      id: -1,
      name: '',
    };

    BrandsAssignState.selectedParent = initialSelected;
    BrandsAssignState.initialSelectedParentId = initialSelected.id;

    onSuccess();
  },

  initBrandList: async (onSuccess: () => void) => {
    BrandsAssignState.isLoadingList = true;

    try {
      const response = await inventoryApi.getBrandList(BrandsState.selectedOutlet.key, {
        deep: false,
        principal_id: null,
        size: BRAND_LIST_SIZE,
      });

      BrandsAssignState.brandList = response;

      const selectedBrandIds = BrandsFormState.brandList.map((brand) => brand.id);

      BrandsAssignState.initialSelectedBrandIds = selectedBrandIds;
      BrandsAssignState.selectedBrandList = BrandsFormState.brandList.slice();
      BrandsAssignState.unselectedBrandList = response.results.filter((brand) => !selectedBrandIds.includes(brand.id));

      onSuccess();
    } catch (e) {
      // TODO: handle error
      /* istanbul ignore next */
      handleMutationError(e);
    } finally {
      BrandsAssignState.isLoadingList = false;
    }
  },
});

export { BrandsAssignState, type IBrandsAssignState, isEmpty, isDirty };
